import { createTheme, ThemeOptions } from '@mui/material/styles';

const colors = {
	orange: '#FF842B',
	orangeInfo: '#F5A623',
	lightOrange: '#f59e23',
	darkGray: '#343A40',
	gray: '#7B809A',
	lightGray: '#CED4DA',
	offWhite: '#E9ECEF',
	veryLightGray: '#F8F9FA',
	blue: '#1A73E8',
	red: '#CE0E0E',
	brightRed: '#FD3333',
	yellow: '#FFAA2B',
	cream: '#FFFCFA',
	veryLightCyan: '#F8FFFE',
	lightBlue: '#EBFDF9',
	teal: '#00AB89',
	darkTeal: '#038877',
	lightTeal: '#009B7D',
	lighterTeal: '#A2DFD3',
	contrastTextWhite: '#fff',
	lightGreen: '#E9F3EB',
	veryLightBeige: '#FAF7F5',
	mediumGray: '#757575',
	softPeach: '#FDF2E9',
	stormGray: '#6B7C85',
	slateBlue: '#344767',
};

// Define ColorKeys type based on the colors object keys
type ColorKeys = keyof typeof colors;

// Extend Palette and PaletteOptions with custom colors under a 'custom' property
declare module '@mui/material/styles/createPalette' {
	interface Palette {
		custom: {
			[key in ColorKeys]: string;
		};
	}

	interface PaletteOptions {
		custom?: {
			[key in ColorKeys]?: string;
		};
	}
}

const boxShadows = {
	main: '0px 2px 6px rgba(0, 0, 0, 0.1)',
	primary: '0px 2px 6px 0px rgba(0, 0, 0, 0.251)',
	secondary: '0px 2px 6px 0px #00000040',
};

declare module '@mui/material/styles' {
	interface Theme {
		boxShadows: {
			[key in keyof typeof boxShadows]: string;
		};
	}

	interface ThemeOptions {
		boxShadows?: {
			[key in keyof typeof boxShadows]?: string;
		};
	}
}

// Create the theme with custom colors and components
export const theme = createTheme({
	palette: {
		primary: {
			main: colors.teal,
			light: colors.lightTeal,
			dark: colors.darkTeal,
			contrastText: colors.contrastTextWhite,
		},
		custom: {
			...colors,
		},
	},
	boxShadows: {
		...boxShadows,
	},
	components: {
		MuiOutlinedInput: {
			styleOverrides: {
				root: {
					borderRadius: '8px',
				},
				notchedOutline: {
					borderRadius: '8px',
				},
			},
		},
		MuiButton: {
			styleOverrides: {
				root: {
					borderRadius: '8px',
					padding: '10px 20px',
				},
			},
		},
		MuiInput: {
			styleOverrides: {
				root: {
					borderRadius: '18px',
				},
			},
		},
		MuiSelect: {
			defaultProps: {
				variant: 'outlined',
			},
			styleOverrides: {
				root: {
					'& fieldset': {
						borderRadius: '8px',
						border: `1px solid ${colors.gray}`, // This controls the initial border color for Root
					},
				},
			},
		},
		MuiTextField: {
			defaultProps: {
				variant: 'outlined',
			},
			styleOverrides: {
				root: {
					borderRadius: '8px',
					width: '100%',
					'&.MuiTextField-sizeSmall': {
						'& .MuiInputBase-root': {
							height: '50px',
						},
						'& .MuiInputBase-input': {
							padding: '12px 10px',
						},
					},
					'& fieldset': {
						borderRadius: '8px',
						border: `1px solid ${colors.gray}`,
					},
					'& .MuiInputBase-input': {
						color: colors.gray,
					},
					'& .MuiInputLabel-asterisk': {
						color: colors.brightRed,
					},
				},
			},
		},
	},
} as ThemeOptions);

export const themeColor = theme.palette.custom;

export const themeBoxShadow = theme.boxShadows;
